package com.kinoshazam.screens.password.compact

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.kinoshazam.screens.details.medium.DialogContent
import com.kinoshazam.uiKit.button.ButtonContentState
import com.kinoshazam.uiKit.button.UiKitButton
import com.kinoshazam.uiKit.button.UiKitButtonState
import com.kinoshazam.uiKit.components.textField.compact.CompactTextField
import com.kinoshazam.uiKit.components.textLabel.TextLabel
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.extension.uiKitImePadding
import com.kinoshazam.uiKit.theme.BoldLarge
import com.kinoshazam.uiKit.theme.BoldMedium
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.view.TextFieldPreset
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.change_password_button_title
import kinoshazam.resources.generated.resources.change_password_title
import kinoshazam.resources.generated.resources.current_password_error
import kinoshazam.resources.generated.resources.current_password_hint
import kinoshazam.resources.generated.resources.current_password_label
import kinoshazam.resources.generated.resources.left
import kinoshazam.resources.generated.resources.login_unknown_error_message
import kinoshazam.resources.generated.resources.new_password_hint
import kinoshazam.resources.generated.resources.new_password_label
import kinoshazam.resources.generated.resources.password_error
import kinoshazam.resources.generated.resources.repeat_password_error
import kinoshazam.resources.generated.resources.repeat_password_hint
import kinoshazam.resources.generated.resources.repeat_password_label
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel
import presentation.screens.changePassword.ChangePasswordMVIAction
import presentation.screens.changePassword.ChangePasswordState
import presentation.screens.changePassword.ChangePasswordViewModel
import utils.FormFactor

@Composable
fun CompactChangePasswordContentHost(
    formFactor: FormFactor = FormFactor.Compact,
) {
    val viewModel = koinViewModel<ChangePasswordViewModel>()
    val state by viewModel.state.collectAsStateWithLifecycle()

    when (formFactor) {
        FormFactor.Expanded -> {
            Scaffold(
                modifier = Modifier
                    .fillMaxSize()
                    .background(UiKitTheme.colors.transparent),
                containerColor = UiKitTheme.colors.transparent,
            ) {
                DialogContent(
                    onClose = { viewModel.onAction(ChangePasswordMVIAction.BackClicked) },
                ) {
                    CompactChangePasswordContent(
                        state = state,
                        onAction = viewModel::onAction,
                        asDialog = true,
                    )
                }
            }
        }

        else -> CompactChangePasswordContent(
            state = state,
            onAction = viewModel::onAction,
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CompactChangePasswordContent(
    state: ChangePasswordState,
    modifier: Modifier = Modifier,
    asDialog: Boolean = false,
    onAction: (ChangePasswordMVIAction) -> Unit,
) {

    val snackbarHostState = remember { SnackbarHostState() }
    val unknownError = stringResource(Res.string.login_unknown_error_message)
    LaunchedEffect(state) {
        if (state.isError) {
            snackbarHostState.showSnackbar(unknownError)
            onAction(ChangePasswordMVIAction.ErrorDismissed)
        }
    }

    Scaffold(
        modifier = modifier
            .then(if (asDialog) Modifier.fillMaxWidth() else Modifier.fillMaxSize())
            .background(UiKitTheme.colors.color_3),
        containerColor = UiKitTheme.colors.color_3,
        snackbarHost = {
            SnackbarHost(
                modifier = Modifier.navigationBarsPadding().uiKitImePadding(),
                hostState = snackbarHostState,
            )
        },
        topBar = {
            TopAppBar(
                modifier = if (asDialog) Modifier else Modifier.statusBarsPadding(),
                title = {
                    TextLabel(
                        state = TextLabelState(
                            text = stringResource(Res.string.change_password_title),
                            color = UiKitTheme.colors.color_2,
                            textStyle = BoldLarge
                        )
                    )
                },
                navigationIcon = {
                    if (!asDialog) {
                        Image(
                            painter = painterResource(Res.drawable.left),
                            colorFilter = ColorFilter.tint(UiKitTheme.colors.color_1),
                            contentDescription = null,
                            modifier = Modifier
                                .padding(horizontal = 16.dp)
                                .clickable {
                                    onAction(ChangePasswordMVIAction.BackClicked)
                                }
                        )
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = UiKitTheme.colors.color_3,
                ),
            )
        },
        content = { paddingValues ->
            ContentScreen(paddingValues, state, onAction, asDialog)
        }
    )
}

@Composable
private fun ContentScreen(
    paddingValues: PaddingValues,
    state: ChangePasswordState,
    onAction: (ChangePasswordMVIAction) -> Unit,
    asDialog: Boolean = false,
) {
    Column(
        modifier = Modifier
            .then(if (asDialog) Modifier.fillMaxWidth() else Modifier.fillMaxSize())
            .padding(paddingValues)
            .uiKitImePadding()
            .verticalScroll(rememberScrollState())
    ) {
        if (!asDialog) {
            Spacer(modifier = Modifier.weight(1f))
        }

        if (state.token == null) {
            CompactTextField(
                modifier = Modifier.padding(16.dp),
                state = TextFieldPreset.passwordState(
                    text = state.currentPassword,
                    hint = TextLabelState(
                        text = stringResource(Res.string.current_password_label),
                        color = UiKitTheme.colors.color_6,
                    ),
                    placeholder = TextLabelState(
                        text = stringResource(Res.string.current_password_hint),
                        color = UiKitTheme.colors.color_6
                    ),
                    error = TextLabelState(
                        text = stringResource(Res.string.current_password_error),
                        color = UiKitTheme.colors.error
                    ).takeIf { state.isCurrentPasswordError },
                    passwordVisible = state.isCurrentPasswordVisible,
                ),
                onValueChanged = {
                    onAction(ChangePasswordMVIAction.CurrentPasswordChanged(it))
                },
                onRightIconClick = {
                    // открыть текущий пароль
                }
            )
        }

        CompactTextField(
            modifier = Modifier.padding(16.dp),
            state = TextFieldPreset.passwordState(
                text = state.newPassword,
                hint = TextLabelState(
                    text = stringResource(Res.string.new_password_label),
                    color = UiKitTheme.colors.color_6,
                ),
                placeholder = TextLabelState(
                    text = stringResource(Res.string.new_password_hint),
                    color = UiKitTheme.colors.color_6
                ),
                error = TextLabelState(
                    text = stringResource(Res.string.password_error),
                    color = UiKitTheme.colors.error
                ).takeIf { state.isNewPasswordError },
                passwordVisible = state.isNewPasswordVisible,
            ),
            onValueChanged = {
                onAction(ChangePasswordMVIAction.NewPasswordChanged(it))
            },
            onRightIconClick = {
                // open password
            }
        )

        CompactTextField(
            modifier = Modifier.padding(16.dp),
            state = TextFieldPreset.passwordState(
                text = state.confirmPassword,
                hint = TextLabelState(
                    text = stringResource(Res.string.repeat_password_label),
                    color = UiKitTheme.colors.color_6,
                ),
                placeholder = TextLabelState(
                    text = stringResource(Res.string.repeat_password_hint),
                    color = UiKitTheme.colors.color_6
                ),
                error = TextLabelState(
                    text = stringResource(Res.string.repeat_password_error),
                    color = UiKitTheme.colors.error
                ).takeIf { state.isConfirmPasswordError },
                passwordVisible = state.isConfirmPasswordVisible,
            ),
            onValueChanged = {
                onAction(ChangePasswordMVIAction.ConfirmPasswordChanged(it))
            },
            onRightIconClick = {
                // open password
            }
        )

        if (!asDialog) {
            Spacer(modifier = Modifier.weight(1f))
        } else {
            Spacer(modifier = Modifier.height(24.dp))
        }

        UiKitButton(
            state = UiKitButtonState(
                title = TextLabelState(
                    text = stringResource(Res.string.change_password_button_title),
                    textStyle = BoldMedium,
                    color = UiKitTheme.colors.color_7,
                ),
                contentState = if (state.isLoading) ButtonContentState.LOADING else ButtonContentState.ENABLED,
                backgroundColor = UiKitTheme.colors.color_1
            ),
            modifier = Modifier
                .fillMaxWidth()
                .padding(16.dp)
                .height(48.dp),
            onClick = {
                onAction(ChangePasswordMVIAction.ChangeClicked)
            }
        )

        Spacer(modifier = Modifier.navigationBarsPadding())
    }
}
